// src/hooks/useCameraControls.ts
import { useCallback, useEffect, useState } from 'react';

type ExtendedConstraint = MediaTrackConstraintSet & {
  zoom?: number;
  focusMode?: 'manual' | 'single-shot' | 'continuous';
};

export function useCameraControls(track: MediaStreamTrack | null) {
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 1 });
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!track) return;

    const capabilities = track.getCapabilities?.() as MediaTrackCapabilities & {
      zoom?: { min: number; max: number };
      focusMode?: string[];
    };
    const settings = track.getSettings?.() as MediaTrackSettings & {
      zoom?: number;
    };

    if (capabilities?.zoom) {
      const min = capabilities.zoom.min ?? 1;
      const max = capabilities.zoom.max ?? 1;

      setZoomRange({ min, max });
      setZoom(settings?.zoom ?? min);
    }

    const focusConstraint: ExtendedConstraint = { focusMode: 'continuous' };

    track
      .applyConstraints({
        advanced: [focusConstraint],
      })
      .catch(console.warn);
  }, [track]);

  const applyZoom = useCallback(
    (value: number) => {
      if (!track?.applyConstraints) return;

      const clamped = Math.min(Math.max(value, zoomRange.min), zoomRange.max);
      const zoomConstraint: ExtendedConstraint = { zoom: clamped };

      track.applyConstraints({ advanced: [zoomConstraint] }).catch(console.warn);
      setZoom(clamped);
    },
    [track, zoomRange]
  );

  return { zoom, setZoom: applyZoom };
}
